/**
 * Projetista do executivo de cada ambiente do Legado: as duas células da
 * tela Elaboração do executivo (quem e desde quando), a faixa de carga por
 * projetista e o bloco que a linha do tempo do ambiente mostra na etapa
 * "Projeto executivo".
 *
 * A planilha não guarda nada disto — vem do backend (`useProjetistasExecutivo`)
 * e é ligado ao ambiente pela chave "PEDIDO:AMBIENTE". Quem não tem
 * `executivo:atribuir_projetista` vê texto, nunca controle.
 */
import React, { useEffect, useState } from "react";

import { useExportRender } from "../../context/ExportRenderContext";
import { useToast } from "../../context/ToastContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import useProjetistasExecutivo from "../../hooks/useProjetistasExecutivo";
import { mq } from "../../styles/canonical/breakpoints";
import {
  SEM_PROJETISTA,
  atribuicaoDe,
  chegouNoExecutivo,
  dataBR,
  executivoFinalizado,
  isoDoDia,
  itemDoAmbiente,
  passagensDoAmbiente,
  resumoDeCarga,
} from "./regras";
import {
  AcoesDoEditor,
  Bloqueado,
  BlocoDaEtapa,
  BotaoTexto,
  Campo,
  Carga,
  DataDaCelula,
  Editor,
  Empurrar,
  Ficha,
  LinhaDaPassagem,
  Nota,
  RotuloDaCarga,
  SelectDaCelula,
} from "./styles";

const VAZIO = "—";

/** Inclui o projetista atual mesmo que ele tenha saído da lista (perdeu a permissão). */
export function opcoesDeProjetista(projetistas, atualId, atualNome) {
  if (!atualId || projetistas.some((p) => p.id === atualId)) return projetistas;
  return [...projetistas, { id: atualId, nome: atualNome || "Projetista" }];
}

function naoPropagar(evento) {
  // A linha da tabela abre o painel do ambiente ao ser clicada.
  evento.stopPropagation();
}

/** Só texto quando a tabela está sendo exportada, em cards (celular/tablet) ou sem permissão. */
function useSomenteLeitura() {
  const { renderAll } = useExportRender() || {};
  const compacto = useMediaQuery(mq.tablet);
  const { podeAtribuir } = useProjetistasExecutivo();
  return Boolean(renderAll) || compacto || !podeAtribuir;
}

/** Célula "Projetista" da tela Elaboração do executivo. `linha` = linha da tabela. */
export function CelulaProjetista({ linha }) {
  const { projetistas, atribuir } = useProjetistasExecutivo();
  const somenteLeitura = useSomenteLeitura();
  const toast = useToast();
  const [salvando, setSalvando] = useState(false);

  if (somenteLeitura) return linha.projetista || VAZIO;
  if (linha.bloqueioProjetista) return <Bloqueado title={linha.bloqueioProjetista}>{VAZIO}</Bloqueado>;

  async function trocar(evento) {
    const projetistaId = evento.target.value || null;
    setSalvando(true);
    try {
      // Pessoa nova começa hoje; a data se corrige na coluna ao lado.
      await atribuir([itemDoAmbiente(linha.rowPlanilha)], projetistaId, projetistaId ? isoDoDia() : null);
    } catch (erro) {
      toast.error(erro?.message || "Não foi possível salvar o projetista.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <SelectDaCelula
      value={linha.projetistaId || ""}
      $vazio={!linha.projetistaId}
      disabled={salvando}
      onChange={trocar}
      onClick={naoPropagar}
      aria-label={`Projetista de ${linha.ambiente}`}
      title={linha.projetista || "Escolher projetista"}
    >
      <option value="">{linha.projetistaId ? "Retirar projetista" : "Escolher…"}</option>
      {opcoesDeProjetista(projetistas, linha.projetistaId, linha.projetista).map((p) => (
        <option key={p.id} value={p.id}>
          {p.nome}
        </option>
      ))}
    </SelectDaCelula>
  );
}

/** Célula "Início" (quando o projetista começou). Grava ao sair do campo. */
export function CelulaInicio({ linha }) {
  const { atribuir } = useProjetistasExecutivo();
  const somenteLeitura = useSomenteLeitura();
  const toast = useToast();
  const [valor, setValor] = useState(linha.inicioIso || "");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setValor(linha.inicioIso || "");
  }, [linha.inicioIso]);

  if (somenteLeitura || !linha.projetistaId || linha.bloqueioProjetista) {
    return dataBR(linha.inicioIso) || VAZIO;
  }

  async function gravar() {
    if (!valor || valor === linha.inicioIso) {
      setValor(linha.inicioIso || "");
      return;
    }
    setSalvando(true);
    try {
      await atribuir([itemDoAmbiente(linha.rowPlanilha)], linha.projetistaId, valor);
    } catch (erro) {
      setValor(linha.inicioIso || "");
      toast.error(erro?.message || "Não foi possível salvar a data de início.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <DataDaCelula
      type="date"
      value={valor}
      max={isoDoDia()}
      disabled={salvando}
      onChange={(evento) => setValor(evento.target.value)}
      onBlur={gravar}
      onKeyDown={(evento) => {
        if (evento.key === "Enter") evento.currentTarget.blur();
      }}
      onClick={naoPropagar}
      aria-label={`Início do projetista em ${linha.ambiente}`}
    />
  );
}

/**
 * Faixa acima da tabela: quantos ambientes cada projetista tem. Cada ficha
 * filtra a tabela por aquela pessoa; "Todos" (ou clicar de novo na mesma
 * ficha) desfaz o filtro.
 */
export function CargaPorProjetista({ linhas, filtro, onFiltro, onLote }) {
  const { podeAtribuir } = useProjetistasExecutivo();
  const { renderAll } = useExportRender() || {};
  const carga = resumoDeCarga(linhas);
  const temAtribuicao = carga.some((item) => item.nome !== SEM_PROJETISTA);
  if (!temAtribuicao && !podeAtribuir) return null;

  return (
    <Carga aria-label="Ambientes por projetista">
      <RotuloDaCarga>Projetistas</RotuloDaCarga>
      <Ficha type="button" $ativo={!filtro} aria-pressed={!filtro} onClick={() => onFiltro(null)}>
        Todos <strong>{linhas.length}</strong>
      </Ficha>
      {carga.map((item) => (
        <Ficha
          key={item.nome}
          type="button"
          $ativo={filtro === item.nome}
          aria-pressed={filtro === item.nome}
          onClick={() => onFiltro(filtro === item.nome ? null : item.nome)}
        >
          {item.nome} <strong>{item.total}</strong>
        </Ficha>
      ))}
      <Empurrar />
      {podeAtribuir && !renderAll && onLote && (
        <BotaoTexto type="button" onClick={onLote}>
          Atribuir em lote
        </BotaoTexto>
      )}
    </Carga>
  );
}

function periodo(passagem) {
  const inicio = dataBR(passagem.inicio);
  const fim = dataBR(passagem.fim);
  if (passagem.atual && !fim) return `desde ${inicio}`;
  return `${inicio} a ${fim}`;
}

/**
 * Bloco da etapa "Projeto executivo" na linha do tempo do ambiente: quem é o
 * projetista, desde quando, e quem esteve antes. `row` = linha CRUA da
 * planilha. Quem tem a permissão corrige por aqui, inclusive depois de o
 * executivo ser finalizado (a tabela trava nesse ponto: o ambiente sai dela).
 */
export function ProjetistaNaLinhaDoTempo({ row, bloqueio }) {
  const { atribuicoes, projetistas, podeAtribuir, atribuir, carregado } = useProjetistasExecutivo();
  const toast = useToast();
  const atribuicao = atribuicaoDe(row, atribuicoes);
  const [editando, setEditando] = useState(false);
  const [projetistaId, setProjetistaId] = useState("");
  const [inicio, setInicio] = useState("");
  const [salvando, setSalvando] = useState(false);

  // Finalizado: a data de término já é a da etapa, logo acima deste bloco.
  const finalizado = executivoFinalizado(row);
  const passagens = passagensDoAmbiente(atribuicao);
  const podeEditar = podeAtribuir && chegouNoExecutivo(row) && !bloqueio && Boolean(itemDoAmbiente(row));

  const avisoDeBloqueio = podeAtribuir && chegouNoExecutivo(row) && bloqueio ? bloqueio : null;

  if (!carregado || (!passagens.length && !podeEditar && !avisoDeBloqueio)) return null;

  function abrir() {
    setProjetistaId(atribuicao?.projetista_id || "");
    setInicio(atribuicao?.inicio || isoDoDia());
    setEditando(true);
  }

  async function salvar() {
    setSalvando(true);
    try {
      await atribuir([itemDoAmbiente(row)], projetistaId || null, projetistaId ? inicio || isoDoDia() : null);
      setEditando(false);
    } catch (erro) {
      toast.error(erro?.message || "Não foi possível salvar o projetista.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <BlocoDaEtapa>
      {passagens.map((passagem) => (
        <LinhaDaPassagem key={`${passagem.projetista_id}-${passagem.inicio}-${passagem.fim}`} $atual={passagem.atual}>
          <strong>{passagem.projetista}</strong>
          <span>
            {passagem.atual && finalizado ? `início ${dataBR(passagem.inicio)}` : periodo(passagem)}
          </span>
        </LinhaDaPassagem>
      ))}
      {!passagens.some((p) => p.atual) && <span>Sem projetista definido</span>}
      {avisoDeBloqueio && <Nota>{avisoDeBloqueio}</Nota>}
      {podeEditar && !editando && (
        <div>
          <BotaoTexto type="button" onClick={abrir}>
            {atribuicao?.projetista_id ? "Alterar projetista" : "Definir projetista"}
          </BotaoTexto>
        </div>
      )}
      {editando && (
        <Editor>
          <Campo>
            Projetista
            <select value={projetistaId} onChange={(e) => setProjetistaId(e.target.value)}>
              <option value="">Sem projetista</option>
              {opcoesDeProjetista(projetistas, atribuicao?.projetista_id, atribuicao?.projetista).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo>
            Início
            <input
              type="date"
              value={projetistaId ? inicio : ""}
              max={isoDoDia()}
              disabled={!projetistaId}
              onChange={(e) => setInicio(e.target.value)}
            />
          </Campo>
          <AcoesDoEditor>
            <BotaoTexto type="button" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : "Salvar"}
            </BotaoTexto>
            <BotaoTexto type="button" onClick={() => setEditando(false)} disabled={salvando}>
              Cancelar
            </BotaoTexto>
          </AcoesDoEditor>
          <Nota>Fica só no sistema; a planilha não muda. O projetista é avisado no sino.</Nota>
        </Editor>
      )}
    </BlocoDaEtapa>
  );
}
